import { useEffect, useState } from 'react';
import { debug } from '../utils/debug';
import type { TelegramWebApp } from '../types';

interface UseTelegramReturn {
  tg: TelegramWebApp | null;
  user: TelegramWebApp['initDataUnsafe']['user'] | null;
  isReady: boolean;
  isTelegramEnv: boolean;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramWebApp['themeParams'];
}

export const useTelegram = (): UseTelegramReturn => {
  const [isReady, setIsReady] = useState(false);
  const [isTelegramEnv, setIsTelegramEnv] = useState(false);
  const [tg, setTg] = useState<TelegramWebApp | null>(null);

  useEffect(() => {
    debug.log('🔍 useTelegram - SDK check:', {
      windowTelegram: !!window.Telegram,
      webApp: !!window.Telegram?.WebApp,
      initData: window.Telegram?.WebApp?.initData,
      user: window.Telegram?.WebApp?.initDataUnsafe?.user,
    });

    let retryTimer: ReturnType<typeof setTimeout>;

    const initTelegram = () => {
      if (window.Telegram?.WebApp) {
        const telegram = window.Telegram.WebApp;
        setTg(telegram);
        setIsTelegramEnv(true);

        debug.log('✅ useTelegram - Initializing Telegram WebApp');

        telegram.ready();
        telegram.expand();
        setIsReady(true);
        telegram.MainButton.hide();
      } else {
        debug.warn('⚠️ useTelegram - Telegram WebApp is not available, retrying...');
        retryTimer = setTimeout(initTelegram, 100);
      }
    };

    // If no Telegram in 1.5 s, give up and mark ready (browser mode)
    const fallbackTimer = setTimeout(() => {
      clearTimeout(retryTimer);
      if (!window.Telegram?.WebApp) {
        debug.warn('⚠️ useTelegram - timeout reached, running in browser mode');
        setIsTelegramEnv(false);
        setIsReady(true);
      }
    }, 1500);

    initTelegram();

    return () => {
      clearTimeout(retryTimer);
      clearTimeout(fallbackTimer);
    };
  }, []);

  return {
    tg,
    user: tg?.initDataUnsafe?.user || null,
    isReady,
    isTelegramEnv,
    colorScheme: tg?.colorScheme || 'light',
    themeParams: tg?.themeParams || {},
  };
};

// Hook for haptic feedback
export const useHapticFeedback = () => {
  const { tg } = useTelegram();

  const impact = (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.impactOccurred(style);
    }
  };

  const notification = (type: 'error' | 'success' | 'warning') => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.notificationOccurred(type);
    }
  };

  const selection = () => {
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.selectionChanged();
    }
  };

  return { impact, notification, selection };
};

// Hook for main button control
export const useMainButton = () => {
  const { tg } = useTelegram();

  const show = (text: string, onClick: () => void) => {
    if (tg?.MainButton) {
      tg.MainButton.setText(text);
      tg.MainButton.onClick(onClick);
      tg.MainButton.show();
    }
  };

  const hide = () => {
    if (tg?.MainButton) {
      tg.MainButton.hide();
    }
  };

  const showProgress = () => {
    if (tg?.MainButton) {
      tg.MainButton.showProgress();
    }
  };

  const hideProgress = () => {
    if (tg?.MainButton) {
      tg.MainButton.hideProgress();
    }
  };

  return { show, hide, showProgress, hideProgress };
}; 